// =============================================================================
// [ファイル] useAssistantMailAutomation.ts
// [概要]   AI エージェントのメール自動処理 (Gmail watch → Pub/Sub → AI 返信下書き) を
//          画面 (エージェント設定「自動処理」タブ) で扱うためのフック。
//          - 状態取得: 本人の受信トレイの監視状態・ルール・処理履歴・今日の件数
//          - 監視の再登録 (期限切れ・配管確認)
//          - ルールの作成・更新・削除 (本人の受信トレイのみ。有効なら監視をバックエンドが確保する)
//          - テスト投入: 条件に一致する直近メールを探し、指定メールを同じ経路で AI に流す
//          いずれも Firebase ID トークンで本人確認するバックエンド API を呼ぶ。
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getIdToken } from '../firebase/getIdToken';
import ep from '../api/endpoints';

/** Gmail の監視 (users.watch) の状態。registered=false は未登録 */
export interface MailAutomationWatch {
  registered: boolean;
  expired: boolean; // 期限 (7 日) を過ぎている
  stale: boolean; // 最終通知から 24 時間以上経っている (配管の疑い)
  expiration: string | null;
  last_push_at: string | null;
  updated_at: string | null;
  history_id: string;
  topic: string;
}

/** ルールの動作。draft_reply = 返信の下書きを作る / summary_only = 要約して通知するだけ */
export type MailRuleAction = 'draft_reply' | 'summary_only';

export const MAIL_RULE_ACTION_LABELS: Record<MailRuleAction, string> = {
  draft_reply: '返信の下書きを作る',
  summary_only: '要約して通知するだけ',
};

/** ルールが参照するエージェント定義 (id・名前・種類。バックエンドが解決して返す。defs は優先順) */
export interface MailRuleRefs {
  defs: { id: string; name: string; type: 'rule' | 'skill' }[];
  issues: { id: string; name: string; status: string }[];
}

/** 自動処理のルール (Firestore HozenallCloud_Assistant_MailRules。本人の受信トレイ分のみ返る)。
 *  「どう書くか」は参照するルール・スキル (個人の定義。混在の順序付き、有効化には 1 件以上) が正本。
 *  自動処理ルール自体は条件・保存先・動作と参照先だけを持つ */
export interface MailAutomationRule {
  id: string;
  mailbox: string;
  name: string;
  enabled: boolean;
  from_email: string;
  subject_contains: string;
  group_id: string;
  group_name: string;
  title_format: string;
  def_ids: string[]; // ルール・スキルの id (先に並ぶものを優先)
  issue_ids: string[];
  note: string; // 補足の指示 (任意)
  action: MailRuleAction;
  // 返信下書きに必ず付ける固定の宛先 (カンマ区切り。AI ではなくここで決める)
  fixed_cc?: string;
  fixed_bcc?: string;
  action_label?: string;
  refs?: MailRuleRefs;
  problems?: string[]; // 処理を止める問題 (スキルが共有に変わった等)
  warnings?: string[]; // 止めない注意 (取組み課題が見えない等)
  created_at: string | null;
  updated_at: string | null;
}

/** ルールの入力 (保存・テスト投入に渡す。id 無しは新規) */
export interface MailRuleInput {
  id?: string;
  name: string;
  enabled: boolean;
  from_email: string;
  subject_contains: string;
  group_id: string;
  title_format: string;
  def_ids: string[];
  issue_ids: string[];
  note: string;
  action: MailRuleAction;
  fixed_cc: string;
  fixed_bcc: string;
}

/** テスト投入の候補 (条件に一致した直近メール。processed_status があれば処理済み) */
export interface MailTestCandidate {
  message_id: string;
  from: string;
  subject: string;
  date: string;
  snippet: string;
  processed_status: string;
  processed_session_id: string;
}

/** 通知ごとの処理記録 (status: queued=会話を作成 / skipped=対象外 / error / processing) */
export interface MailAutomationRecord {
  id: string;
  created_at: string | null;
  from: string;
  subject: string;
  status: string;
  rule_id: string;
  session_id: string;
  reason: string;
  error: string;
  manual: boolean;
}

export interface MailAutomationStatus {
  mailbox: string;
  watch: MailAutomationWatch;
  rules: MailAutomationRule[];
  records: MailAutomationRecord[];
  today: { notified: number; matched: number; errors: number };
  renew_schedule: string;
}

const STATUS_URL = ep('mail_automation_status');
const REGISTER_URL = ep('mail_automation_register');
const RULE_SAVE_URL = ep('mail_rule_save');
const RULE_DELETE_URL = ep('mail_rule_delete');
const RULE_TEST_CANDIDATES_URL = ep('mail_rule_test_candidates');
const RULE_TEST_RUN_URL = ep('mail_rule_test_run');

/** 本人の ID トークン付きで POST し、status=success 以外とネットワーク失敗は Error にする */
async function call<T = Record<string, unknown>>(url: string, body: Record<string, unknown>): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error('未ログインです');
  try {
    const res = await axios.post(url, body, { headers: { Authorization: `Bearer ${token}` } });
    if (res.data?.status !== 'success') throw new Error(res.data?.message || '処理に失敗しました');
    return res.data as T;
  } catch (e) {
    // 400 (入力不備) などはサーバーのメッセージをそのまま見せる
    if (axios.isAxiosError(e) && e.response?.data?.message) throw new Error(String(e.response.data.message));
    throw e;
  }
}

/**
 * @param enabled true の間だけ取得する (タブを開いた時に取り、閉じている間は呼ばない)
 */
export function useAssistantMailAutomation(enabled: boolean) {
  const [status, setStatus] = useState<MailAutomationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await call<MailAutomationStatus>(STATUS_URL, {});
      setStatus(data);
    } catch (e) {
      console.error('メール自動処理の状態取得に失敗しました', e);
      setError(e instanceof Error ? e.message : '状態の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  /** 監視を登録し直す。成功したら最新の状態を取り直す。失敗時は例外 (呼び元が通知する) */
  const register = useCallback(async () => {
    setRegistering(true);
    try {
      await call(REGISTER_URL, {});
      await refresh();
    } finally {
      setRegistering(false);
    }
  }, [refresh]);

  /** ルールの作成・更新。返りの一覧で状態を更新する */
  const saveRule = useCallback(async (rule: MailRuleInput): Promise<{ id: string; watch_registered: boolean }> => {
    const data = await call<{ id: string; watch_registered: boolean; rules: MailAutomationRule[] }>(RULE_SAVE_URL, { rule });
    setStatus((prev) => (prev ? { ...prev, rules: data.rules } : prev));
    return { id: data.id, watch_registered: data.watch_registered };
  }, []);

  const deleteRule = useCallback(async (id: string) => {
    const data = await call<{ rules: MailAutomationRule[] }>(RULE_DELETE_URL, { id });
    setStatus((prev) => (prev ? { ...prev, rules: data.rules } : prev));
  }, []);

  /** 条件 (差出人・件名) に一致する直近 30 日のメールを探す (読み取りのみ) */
  const testCandidates = useCallback(async (rule: MailRuleInput): Promise<MailTestCandidate[]> => {
    const data = await call<{ candidates: MailTestCandidate[] }>(RULE_TEST_CANDIDATES_URL, { rule });
    return data.candidates ?? [];
  }, []);

  /** 指定メールをこのルールで投入する (会話と AI 実行が走る。送信はしない) */
  const testRun = useCallback(async (rule: MailRuleInput, messageId: string): Promise<string> => {
    const data = await call<{ session_id: string }>(RULE_TEST_RUN_URL, { rule, message_id: messageId });
    return data.session_id;
  }, []);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);

  return { status, loading, registering, error, refresh, register, saveRule, deleteRule, testCandidates, testRun };
}

export default useAssistantMailAutomation;
